import { api } from '@/helpers/apiClient'
import logger, { toErrorMessage } from '@/helpers/logger'
import {
  treeSpeciesDtoSchema,
  treeSpeciesListDtoSchema,
  type TreeSpeciesDto,
  type TreeSpeciesListDto,
} from '@lib/dto/tree-species.dto'
import type {
  CreateTreeSpeciesInput,
  UpdateTreeSpeciesInput,
} from '@lib/input/tree-species.input'
import type { ITreeSpeciesService } from '@/types/TreeSpeciesService.types'

/** The species catalog. Responses validated against the shared schemas. */
class TreeSpeciesService implements ITreeSpeciesService {
  async list(): Promise<TreeSpeciesListDto> {
    try {
      return await api.get('/tree-species', treeSpeciesListDtoSchema)
    } catch (error) {
      logger.error('Failed to load tree species', {
        error: toErrorMessage(error),
      })
      throw error
    }
  }

  async create(input: CreateTreeSpeciesInput): Promise<TreeSpeciesDto> {
    try {
      return await api.post('/tree-species', input, treeSpeciesDtoSchema)
    } catch (error) {
      logger.error('Failed to create tree species', {
        error: toErrorMessage(error),
      })
      throw error
    }
  }

  async update(
    id: string,
    input: UpdateTreeSpeciesInput,
  ): Promise<TreeSpeciesDto> {
    try {
      return await api.put(
        `/tree-species/${encodeURIComponent(id)}`,
        input,
        treeSpeciesDtoSchema,
      )
    } catch (error) {
      logger.error('Failed to update tree species', {
        id,
        error: toErrorMessage(error),
      })
      throw error
    }
  }

  async setArchived(id: string, archived: boolean): Promise<TreeSpeciesDto> {
    const action = archived ? 'archive' : 'restore'
    try {
      return await api.post(
        `/tree-species/${encodeURIComponent(id)}/${action}`,
        {},
        treeSpeciesDtoSchema,
      )
    } catch (error) {
      logger.error(`Failed to ${action} tree species`, {
        id,
        error: toErrorMessage(error),
      })
      throw error
    }
  }
}

export const treeSpeciesService = new TreeSpeciesService()
